import { Html5Qrcode } from 'html5-qrcode'
import { useCallback, useEffect, useRef } from 'react'

export interface CameraCapabilities {
  hasTorch: boolean
  hasMultipleCameras: boolean
}

interface QrScannerProps {
  onScanSuccess: (decodedText: string) => void
  onScanError?: (error: string) => void
  onPermissionDenied?: () => void
  onCapabilitiesChange?: (capabilities: CameraCapabilities) => void
  cameraFacing: 'user' | 'environment'
  paused?: boolean
}

const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return /Mobi|Android|iPhone|iPad|iPod|IEMobile/i.test(navigator.userAgent)
}

const isPermissionError = (err: unknown): boolean => {
  if (err instanceof DOMException) {
    if (
      err.name === 'NotAllowedError' ||
      err.name === 'PermissionDeniedError'
    ) {
      return true
    }
  }

  const rawMessage =
    err instanceof Error ? err.message : typeof err === 'string' ? err : ''
  const msg = rawMessage.toLowerCase()
  return (
    msg.includes('permission denied') ||
    msg.includes('notallowederror') ||
    msg.includes('permission dismissed') ||
    msg.includes('permission not granted')
  )
}

const SCANNER_ELEMENT_ID = 'qr-scanner-region'

export const QrScanner: React.FC<QrScannerProps> = ({
  onScanSuccess,
  onScanError,
  onPermissionDenied,
  onCapabilitiesChange,
  cameraFacing,
  paused = false,
}) => {
  const scannerRef = useRef<Html5Qrcode | null>(null)
  const isRunningRef = useRef(false)

  const stopScanner = useCallback(async () => {
    if (scannerRef.current && isRunningRef.current) {
      try {
        await scannerRef.current.stop()
        isRunningRef.current = false
      } catch {
        // ignore stop errors
      }
    }
  }, [])

  const startScanner = useCallback(async () => {
    if (!scannerRef.current) {
      scannerRef.current = new Html5Qrcode(SCANNER_ELEMENT_ID)
    }

    if (isRunningRef.current) {
      await stopScanner()
    }

    try {
      await scannerRef.current.start(
        { facingMode: cameraFacing },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          disableFlip: false,
        },
        (decodedText) => {
          onScanSuccess(decodedText)
        },
        () => {
          // scan error (no QR found in frame) — silent
        },
      )
      isRunningRef.current = true

      if (onCapabilitiesChange) {
        try {
          const cameras = await Html5Qrcode.getCameras()
          const capabilities =
            scannerRef.current.getRunningTrackCapabilities() as MediaTrackCapabilities & {
              torch?: boolean
            }
          onCapabilitiesChange({
            hasTorch: Boolean(capabilities?.torch),
            hasMultipleCameras: isMobileDevice() && cameras.length > 1,
          })
        } catch {
          onCapabilitiesChange({
            hasTorch: false,
            hasMultipleCameras: false,
          })
        }
      }
    } catch (err) {
      if (isPermissionError(err)) {
        onPermissionDenied?.()
        return
      }
      onScanError?.(
        err instanceof Error ? err.message : 'Failed to start camera',
      )
    }
  }, [
    cameraFacing,
    onScanSuccess,
    onScanError,
    onPermissionDenied,
    onCapabilitiesChange,
    stopScanner,
  ])

  useEffect(() => {
    if (!paused) {
      startScanner()
    } else {
      stopScanner()
    }

    return () => {
      stopScanner()
    }
  }, [paused, startScanner, stopScanner])

  // Restart scanner when camera facing changes
  useEffect(() => {
    if (!paused && isRunningRef.current) {
      stopScanner().then(() => startScanner())
    }
  }, [cameraFacing])

  return (
    <div className="w-full h-full overflow-hidden">
      <div
        id={SCANNER_ELEMENT_ID}
        className="!absolute inset-0 [&_video]:!object-cover [&_video]:!w-full [&_video]:!h-full"
      />
    </div>
  )
}
